import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Workflow, Bot } from "lucide-react";
import ClientPicker from "@/components/ClientPicker";
import ScheduleEditor, { emptySchedule, inputCls } from "./ScheduleEditor";

/**
 * Create / edit a scheduled task: what runs (flow or agent), for which client,
 * with which input, when. Agents can pre-load context through tools such as
 * `connection.call` (SiteCentric data) before the prompt runs.
 */
export default function ScheduledTaskModal({ task, preset, meta, onClose, onSaved }) {
    const editing = !!task;
    const [kind, setKind] = useState(task?.kind || preset?.kind || "flow");
    const [targets, setTargets] = useState([]);
    const [form, setForm] = useState({
        name: task?.name || "", description: task?.description || "",
        target_id: task?.target_id || preset?.target_id || "", client_id: task?.client_id || "",
        site_id: task?.site_id || "", study_id: task?.study_id || "",
        input_payload: JSON.stringify(task?.input_payload || {}, null, 2),
        schedule: task?.schedule || emptySchedule(), enabled: task?.enabled ?? true,
    });
    const [tools, setTools] = useState((task?.context_tools || []).map((t) => ({ ...t, params: JSON.stringify(t.params || {}, null, 2) })));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(kind === "flow" ? "/admin/flows" : "/admin/agents").then(({ data }) => setTargets(data.items || [])).catch(() => setTargets([]));
    }, [kind]);

    const toolCatalog = useMemo(() => (meta?.tools || []).filter((t) => t.name !== "sitecentric.request" && t.name !== "sitecentric.operations"), [meta]);
    const target = targets.find((t) => t.id === form.target_id);

    const submit = async () => {
        let input_payload;
        try { input_payload = form.input_payload.trim() ? JSON.parse(form.input_payload) : {}; } catch { toast.error("Input must be valid JSON"); return; }
        const context_tools = [];
        for (const t of tools) {
            try { context_tools.push({ name: t.name, label: t.label || null, params: t.params.trim() ? JSON.parse(t.params) : {} }); }
            catch { toast.error(`Parameters of ${t.name} must be valid JSON`); return; }
        }
        setSaving(true);
        try {
            const body = {
                name: form.name.trim() || null, description: form.description.trim() || null, target_id: form.target_id,
                client_id: form.client_id, site_id: form.site_id.trim() || null, study_id: form.study_id.trim() || null,
                input_payload, context_tools, schedule: form.schedule, enabled: form.enabled,
            };
            const { data } = editing
                ? await api.patch(`/admin/scheduled-tasks/${task.id}`, body)
                : await api.post("/admin/scheduled-tasks", { ...body, kind });
            toast.success(editing ? "Scheduled task saved" : `Scheduled — ${data.schedule_text}`);
            onSaved(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="scheduled-task-modal">
            <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-[#0c0c0c] border border-white/10 rounded-xl">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 sticky top-0 bg-[#0c0c0c] z-10">
                    <h2 className="text-base">{editing ? "Edit scheduled task" : "New scheduled task"}</h2>
                    <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="scheduled-task-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-5 py-4 space-y-4">
                    {!editing && (
                        <div className="flex gap-2">
                            {[["flow", "Flow", Workflow], ["agent", "AI agent", Bot]].map(([k, label, Icon]) => (
                                <button key={k} type="button" onClick={() => { setKind(k); setForm({ ...form, target_id: "" }); }} data-testid={`scheduled-task-kind-${k}`}
                                    className={`px-3 py-1.5 rounded-md text-xs border inline-flex items-center gap-1.5 ${kind === k
                                        ? "bg-[#7C3AED]/20 border-[#7C3AED]/60 text-[#c4b5fd]" : "border-white/10 text-white/55 hover:text-white"}`}>
                                    <Icon className="w-3.5 h-3.5" /> {label}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-[11px] text-white/55 mb-1">{kind === "flow" ? "Flow" : "AI agent"} <span className="text-rose-400">*</span></div>
                            <select value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} data-testid="scheduled-task-target" className={inputCls}>
                                <option value="">— choose —</option>
                                {targets.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.status} · v{t.active_version_no}</option>)}
                            </select>
                        </label>
                        <ClientPicker value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} label="Runs for client" required includeSuperdomain testId="scheduled-task-client" />
                        <label className="block">
                            <div className="text-[11px] text-white/55 mb-1">Name</div>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={target ? `${target.name} — on schedule` : "optional"}
                                data-testid="scheduled-task-name" className={inputCls} />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <div className="text-[11px] text-white/55 mb-1">Site (optional)</div>
                                <input value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className={inputCls} data-testid="scheduled-task-site" />
                            </label>
                            <label className="block">
                                <div className="text-[11px] text-white/55 mb-1">Study (optional)</div>
                                <input value={form.study_id} onChange={(e) => setForm({ ...form, study_id: e.target.value })} className={inputCls} data-testid="scheduled-task-study" />
                            </label>
                        </div>
                    </div>
                    <label className="block">
                        <div className="text-[11px] text-white/55 mb-1">{kind === "flow" ? "Flow input (JSON)" : "Agent variables (JSON)"}</div>
                        <textarea rows={4} value={form.input_payload} onChange={(e) => setForm({ ...form, input_payload: e.target.value })}
                            data-testid="scheduled-task-input" className={`${inputCls} font-mono text-[11px]`} />
                    </label>

                    {kind === "agent" && (
                        <div className="border border-white/10 rounded-md p-3 space-y-2" data-testid="scheduled-task-tools">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-xs">Context loaded before the agent runs</div>
                                    <div className="text-[10px] text-white/45">e.g. <span className="font-mono">connection.call</span> against a SiteCentric connection — the token is kept for this run and logged out afterwards.</div>
                                </div>
                                <select value="" onChange={(e) => e.target.value && setTools([...tools, { name: e.target.value, label: "", params: JSON.stringify({ connection: "", endpoint: "", params: {} }, null, 2) }])}
                                    data-testid="scheduled-task-add-tool" className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs">
                                    <option value="">+ add tool</option>
                                    {toolCatalog.map((t) => <option key={t.name} value={t.name}>{t.label} ({t.name})</option>)}
                                </select>
                            </div>
                            {tools.length === 0 && <div className="text-[11px] text-white/40">No context tools — the agent runs on its variables alone.</div>}
                            {tools.map((t, i) => (
                                <div key={i} className="border border-white/5 rounded-md p-2 space-y-2" data-testid={`scheduled-task-tool-${i}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-[11px] text-[#c4b5fd]">{t.name}</span>
                                        <input value={t.label} onChange={(e) => setTools(tools.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                                            placeholder="label shown to the agent" className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px]" data-testid={`scheduled-task-tool-label-${i}`} />
                                        <button onClick={() => setTools(tools.filter((_, j) => j !== i))} className="text-white/40 hover:text-rose-300" data-testid={`scheduled-task-tool-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                    <textarea rows={4} value={t.params} onChange={(e) => setTools(tools.map((x, j) => (j === i ? { ...x, params: e.target.value } : x)))}
                                        className={`${inputCls} font-mono text-[11px]`} data-testid={`scheduled-task-tool-params-${i}`} />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="border border-white/10 rounded-md p-3 space-y-2">
                        <div className="text-xs">When</div>
                        <ScheduleEditor value={form.schedule} onChange={(schedule) => setForm({ ...form, schedule })} timezones={meta?.timezones || ["UTC"]} />
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-violet-400" data-testid="scheduled-task-enabled" />
                        Enabled
                    </label>
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/5 sticky bottom-0 bg-[#0c0c0c]">
                    <button onClick={onClose} className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button onClick={submit} disabled={saving || !form.target_id || !form.client_id} data-testid="scheduled-task-save"
                        className="px-3 py-1.5 text-xs bg-white text-black rounded-md disabled:opacity-40 inline-flex items-center gap-1.5">
                        <Plus className="w-3 h-3" /> {saving ? "Saving…" : editing ? "Save" : "Schedule"}
                    </button>
                </div>
            </div>
        </div>
    );
}
